/**
 * Traits are adaptations earned in one stage that carry forward through the
 * rest of the run. Each trait applies small stat modifiers that later stages
 * read generically through RunState.mod(stat).
 *
 * Stage agents own the trait entries whose `from` is their stage and decide
 * when to grant them (via ctx.grantTrait). Keep modifiers small (±5–20%).
 */
import type { StageId } from './tree';

export type TraitStat =
  | 'maxHealth'
  | 'speed'
  | 'damage'
  | 'defense'
  | 'dashCooldown'
  | 'stamina'
  | 'perception'
  | 'regen';

export interface TraitDef {
  id: string;
  name: string;
  description: string;
  from: StageId;
  /** Additive multipliers: +0.1 means +10%. dashCooldown negative = faster. */
  mods: Partial<Record<TraitStat, number>>;
  icon?: string;
}

export const TRAITS: Record<string, TraitDef> = {
  // algae
  oxygenic: { id: 'oxygenic', name: 'Oxygenic Photosynthesis', description: 'Slowly regenerate health.', from: 'algae', mods: { regen: 0.15 } },
  toxin_bloom: { id: 'toxin_bloom', name: 'Toxic Bloom', description: '+10% damage.', from: 'algae', mods: { damage: 0.1 } },
  // jellyfish
  nematocysts: { id: 'nematocysts', name: 'Nematocysts', description: '+10% damage.', from: 'jellyfish', mods: { damage: 0.1 } },
  bioluminescence: { id: 'bioluminescence', name: 'Bioluminescence', description: 'Sense nearby threats sooner.', from: 'jellyfish', mods: { perception: 0.2 } },
  // cell
  tough_membrane: { id: 'tough_membrane', name: 'Tough Membrane', description: '+10% health.', from: 'cell', mods: { maxHealth: 0.1 } },
  flagellum: { id: 'flagellum', name: 'Flagellum', description: '+8% speed.', from: 'cell', mods: { speed: 0.08 } },
  chloroplasts: { id: 'chloroplasts', name: 'Chloroplasts', description: 'Slowly regenerate health.', from: 'cell', mods: { regen: 0.15 } },
  // multicell
  cnidocytes: { id: 'cnidocytes', name: 'Stinging Cells', description: '+10% damage.', from: 'multicell', mods: { damage: 0.1 } },
  notochord: { id: 'notochord', name: 'Notochord', description: '+10% speed.', from: 'multicell', mods: { speed: 0.1 } },
  // fish
  jaws: { id: 'jaws', name: 'Hinged Jaws', description: '+12% damage.', from: 'fish', mods: { damage: 0.12 } },
  swim_bladder: { id: 'swim_bladder', name: 'Swim Bladder', description: 'Dash recovers 10% faster.', from: 'fish', mods: { dashCooldown: -0.1 } },
  lateral_line: { id: 'lateral_line', name: 'Lateral Line', description: 'Sense nearby threats sooner.', from: 'fish', mods: { perception: 0.2 } },
  // amphibian
  sticky_tongue: { id: 'sticky_tongue', name: 'Sticky Tongue', description: '+8% damage.', from: 'amphibian', mods: { damage: 0.08 } },
  strong_legs: { id: 'strong_legs', name: 'Strong Hind Legs', description: '+10% stamina.', from: 'amphibian', mods: { stamina: 0.1 } },
  // reptile
  scales: { id: 'scales', name: 'Scaly Hide', description: '+12% defense.', from: 'reptile', mods: { defense: 0.12 } },
  amniotic_egg: { id: 'amniotic_egg', name: 'Amniotic Egg', description: '+10% health.', from: 'reptile', mods: { maxHealth: 0.1 } },
  // mammal
  fur: { id: 'fur', name: 'Fur', description: 'Regenerate health faster.', from: 'mammal', mods: { regen: 0.2 } },
  whiskers: { id: 'whiskers', name: 'Whiskers', description: 'Sense nearby threats sooner.', from: 'mammal', mods: { perception: 0.2 } },
  big_brain: { id: 'big_brain', name: 'Bigger Brain', description: 'Abilities recharge 10% faster.', from: 'mammal', mods: { dashCooldown: -0.1 } },
  // bird
  hollow_bones: { id: 'hollow_bones', name: 'Hollow Bones', description: '+10% speed.', from: 'bird', mods: { speed: 0.1 } },
  talons: { id: 'talons', name: 'Talons', description: '+10% damage.', from: 'bird', mods: { damage: 0.1 } },
  keen_eyes: { id: 'keen_eyes', name: 'Keen Eyes', description: 'Spot threats from much further away.', from: 'bird', mods: { perception: 0.25 } },
  // whale
  blubber: { id: 'blubber', name: 'Blubber', description: '+15% health.', from: 'whale', mods: { maxHealth: 0.15 } },
  echolocation: { id: 'echolocation', name: 'Echolocation', description: 'Sense threats through the murk.', from: 'whale', mods: { perception: 0.2 } },
  baleen: { id: 'baleen', name: 'Baleen', description: 'Filter-feed: +10% damage.', from: 'whale', mods: { damage: 0.1 } },
  // primate
  opposable_thumbs: { id: 'opposable_thumbs', name: 'Opposable Thumbs', description: '+10% damage.', from: 'primate', mods: { damage: 0.1 } },
  bipedal: { id: 'bipedal', name: 'Bipedalism', description: '+10% stamina.', from: 'primate', mods: { stamina: 0.1 } },
  brachiation: { id: 'brachiation', name: 'Brachiation', description: '+8% speed.', from: 'primate', mods: { speed: 0.08 } },
  // human
  persistence_hunter: { id: 'persistence_hunter', name: 'Persistence Hunter', description: '+15% stamina.', from: 'human', mods: { stamina: 0.15 } },
  firekeeper: { id: 'firekeeper', name: 'Firekeeper', description: 'Regenerate health faster.', from: 'human', mods: { regen: 0.2 } },
  spear_mastery: { id: 'spear_mastery', name: 'Spear Mastery', description: '+12% damage.', from: 'human', mods: { damage: 0.12 } },
  storyteller: { id: 'storyteller', name: 'Storyteller', description: 'Sense threats sooner.', from: 'human', mods: { perception: 0.2 } },
};

export function traitsFrom(stage: StageId): TraitDef[] {
  return Object.values(TRAITS).filter((t) => t.from === stage);
}
